import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";

import { useTranslation } from "utils";

import { SCENARIO_LIMITS } from "consts";

import { SoilSightCoverCropTiming, SoilSightCropDefaultsByCropId, SoilSightNonCropDefaults } from "models";

import { Button, Icon, Tooltip, Typography } from "components";

import { AddScenarioModal } from "pages/app/scenario-analysis/components/AddScenarioModal";
import { ScenarioCard } from "pages/app/scenario-analysis/components/ScenarioCard";
import { ScenarioList } from "pages/app/scenario-analysis/components/ScenarioList";
import { CropDetailsDrawer } from "pages/app/scenario-analysis/components/crop-details-input-context-drawer/CropDetailsInputDrawer";
import { useMainCropOptions } from "pages/app/scenario-analysis/hooks/useMainCropOptions";
import { useScenarioBuilderHandlers } from "pages/app/scenario-analysis/hooks/useScenarioBuilderHandlers";
import { CropYear, InvalidYearIdsByScenario, ScenarioBuilderHandle, ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { SoilSightOutcomesByScenario } from "pages/app/scenario-analysis/utils/forecast";
import { tourTargetProps } from "pages/app/scenario-analysis/utils/outcomesTourTarget";
import {
  buildInheritedCoverCropDefaultsByKey,
  buildInheritedDefaultsByCropId,
  withUpdatedYearData,
} from "pages/app/scenario-analysis/utils/scenarioBuilderState";
import { cumulativeMargin } from "pages/app/scenario-analysis/utils/scenarioComparison";
import type { AutosavedChangeType } from "pages/app/scenario-analysis/utils/soilSightTracking";

interface ScenarioBuilderComponentProps {
  /** The page owns the scenarios; every change goes back through onScenariosChange. */
  scenarios: ScenarioData[];
  /** The same run response the chart and comparison table read. */
  soilSightOutcomes?: SoilSightOutcomesByScenario | null;
  /** Horizon the margin is summed over; without it the card claims no figure. */
  projectionYears?: number;
  /** Figures no longer match the inputs, so the cards wait rather than show the previous run. */
  isStale?: boolean;
  onScenariosChange: (next: ScenarioData[]) => void;
  /** Per-scenario validation errors, set when recalculate is blocked. Keys are scenario IDs. */
  recalcValidationErrors?: InvalidYearIdsByScenario;
  /** Creates the scenario on the server; without it the scenario is added locally with a client-only id. */
  onCreateScenario?: (title: string, templateScenarioId?: string) => Promise<void> | void;
  /** Resolves true once the save has landed. The drawer uses it, and we report the drawer save only on true. */
  onPersistScenarioYears?: (scenarioId: string, years: CropYear[]) => Promise<boolean> | void;
  /** We tag the edit here and report it when its autosave lands. */
  onScenarioChange?: (scenarioId: string, changeType: AutosavedChangeType) => void;
  cropDefaultsByCropId?: SoilSightCropDefaultsByCropId;
  coverCropDefaults?: SoilSightNonCropDefaults["coverCropDefaults"] | null;
  /** Silver's cover-crop timing for the field's region; rows warn when the model would skip a cover crop. */
  coverCropTiming?: SoilSightCoverCropTiming | null;
  /** Called when the user adds or removes a year so the parent can clear validation error highlights. */
  onClearValidationErrors?: () => void;
  /** The section footer sends the farmer back up to the forecast results. */
  onViewResults?: () => void;
}

const ScenarioBuilder = forwardRef<ScenarioBuilderHandle, ScenarioBuilderComponentProps>(
  (
    {
      scenarios,
      soilSightOutcomes = null,
      projectionYears = 0,
      isStale = false,
      onScenariosChange,
      onCreateScenario,
      onPersistScenarioYears,
      onScenarioChange,
      cropDefaultsByCropId,
      coverCropDefaults,
      coverCropTiming = null,
      recalcValidationErrors,
      onClearValidationErrors,
      onViewResults,
    },
    ref
  ) => {
    const { t } = useTranslation("scenarioAnalysis");

    const allYears = useMemo(() => scenarios.flatMap((s) => s.years), [scenarios]);
    const { mainCropOptions, mainCropLabelLookup, isPracticeCropsFetching } = useMainCropOptions(allYears);

    const setScenarios = (updater: ScenarioData[] | ((prev: ScenarioData[]) => ScenarioData[])) => {
      onScenariosChange(typeof updater === "function" ? updater(scenarios) : updater);
    };

    const [isModalVisible, setIsModalVisible] = useState(false);
    const {
      drawerState,
      openCropDetails,
      closeCropDetails,
      handleTitleChange,
      handleDelete,
      handleYearDataChange,
      handleYearRemove,
      handleYearReorder,
      handleRefine,
      handleSaveForYear,
      handleApplyToAll,
      handleAddYear,
      handleAddScenario,
      getInvalidYearIdsForScenario,
      getAddYearErrorMessage,
      getValidationErrorMessage,
    } = useScenarioBuilderHandlers({
      scenarios,
      setScenarios,
      onCreateScenario,
      onPersistScenarioYears,
      onScenarioChange,
      onClearValidationErrors,
      recalcValidationErrors,
    });

    const cardElements = useRef<Map<string, HTMLDivElement>>(new Map());

    useImperativeHandle(ref, () => ({
      openAddScenarioModal: () => setIsModalVisible(true),
      getCardElement: (id: string) => cardElements.current.get(id) ?? null,
      openCropDetailsForFirstYear: () => {
        const first = scenarios[0];
        if (!first || first.years.length === 0) return;
        openCropDetails({ scenarioId: first.id, yearId: first.years[0].id });
      },
    }));

    const modalScenarios = useMemo(() => scenarios.map((s) => ({ id: s.id, title: s.title, isReference: s.isReference })), [scenarios]);

    const referenceScenario = scenarios.find((s) => s.isReference);
    const marginFor = (scenarioId: string): number | null => {
      const rows = soilSightOutcomes?.[scenarioId];
      if (isStale || !rows?.length || projectionYears <= 0) return null;
      return cumulativeMargin(rows, projectionYears);
    };
    const referenceCarbonValue = referenceScenario ? marginFor(referenceScenario.id) : null;
    const isAtScenarioLimit = scenarios.length >= SCENARIO_LIMITS.MAX_SCENARIOS;

    const renderCropDetailsDrawer = () => {
      if (!drawerState) return null;

      const activeScenario = scenarios.find((s) => s.id === drawerState.scenarioId);
      if (!activeScenario) return null;

      const yearIndex = activeScenario.years.findIndex((y) => y.id === drawerState.yearId);
      if (yearIndex === -1) return null;

      const activeYear = activeScenario.years[yearIndex];

      // Only this scenario's own years may pre-fill the drawer, so a refine never reaches another one.
      const inheritanceSource = [activeScenario];
      const inheritedDefaultsByCropId = buildInheritedDefaultsByCropId(inheritanceSource, drawerState.yearId);
      const inheritedCoverCropDefaultsByKey = buildInheritedCoverCropDefaultsByKey(inheritanceSource, drawerState.yearId);

      return (
        <CropDetailsDrawer
          visible
          onClose={closeCropDetails}
          yearIndex={yearIndex + 1}
          cropYear={activeYear}
          mainCropOptions={mainCropOptions}
          mainCropLabelLookup={mainCropLabelLookup}
          onSaveForYear={handleSaveForYear}
          onApplyToAll={handleApplyToAll}
          cropDefaultsByCropId={cropDefaultsByCropId}
          inheritedDefaultsByCropId={inheritedDefaultsByCropId}
          inheritedCoverCropDefaultsByKey={inheritedCoverCropDefaultsByKey}
          coverCropDefaults={coverCropDefaults}
        />
      );
    };

    return (
      <div className="scenario-analysis-wrapper">
        <div className="scenario-analysis-header">
          <div className="scenario-analysis-header-title">
            <Typography variant="h4" bold className="scenario-analysis-header-title-text">
              {t("scenarioBuilder.cropRotationScenarios")}
            </Typography>
            <Tooltip title={t("scenarioBuilder.cropRotationScenariosTooltip")} placement="top">
              <span className="scenario-analysis-header-info" aria-label={t("scenarioBuilder.cropRotationScenariosTooltip")}>
                <Icon iconName="tabler/info-circle" size="s" />
              </span>
            </Tooltip>
            <Typography variant="paragraph-s" className="scenario-analysis-header-subtitle">
              {t("scenarioBuilder.addUpToScenarios", { max: SCENARIO_LIMITS.MAX_SCENARIOS })}
            </Typography>
          </div>

          {/* The button already says what it does, so its tooltip only explains the limit. */}
          <span className="scenario-analysis-header-add-scenario-target" {...tourTargetProps("add-scenario")}>
            <Tooltip title={t("scenarioBuilder.addScenarioLimitReached", { max: SCENARIO_LIMITS.MAX_SCENARIOS })} disabled={!isAtScenarioLimit}>
              <Button
                type="secondary"
                size="middle"
                className="scenario-analysis-header-add-scenario"
                onClick={() => setIsModalVisible(true)}
                disabled={isAtScenarioLimit}
                dataTestId="add-scenario-icon-btn"
                icon={<Icon iconName="klim/plus" size="s" />}
                iconLeft
              >
                {t("scenarioBuilder.addScenario")}
              </Button>
            </Tooltip>
          </span>
        </div>

        <div className="scenario-analysis-scenarios">
          <ScenarioList>
            {scenarios.map((scenario) => (
              <div
                key={scenario.id}
                {...(scenario.isReference ? tourTargetProps("current-rotation") : {})}
                ref={(el) => {
                  if (el) cardElements.current.set(scenario.id, el);
                  else cardElements.current.delete(scenario.id);
                }}
              >
                <ScenarioCard
                  scenarioId={scenario.id}
                  title={scenario.title}
                  dataTestId={`${scenario.id}-scenario`}
                  isBaseline={scenario.isBaseline}
                  isReference={scenario.isReference}
                  years={scenario.years}
                  carbonValue={marginFor(scenario.id)}
                  referenceCarbonValue={referenceCarbonValue}
                  onTitleChange={(newTitle) => handleTitleChange(scenario.id, newTitle)}
                  onDelete={() => handleDelete(scenario.id)}
                  onYearDataChange={(yearId, updates) => handleYearDataChange(scenario.id, yearId, updates)}
                  onApplyCoverCropMix={(yearId, updates) => setScenarios((prev) => withUpdatedYearData(prev, scenario.id, yearId, updates))}
                  onYearRemove={(yearId) => handleYearRemove(scenario.id, yearId)}
                  onYearRefine={(yearId) => handleRefine(scenario.id, yearId)}
                  onYearReorder={(fromIndex, toIndex) => handleYearReorder(scenario.id, fromIndex, toIndex)}
                  onAddYear={() => handleAddYear(scenario.id)}
                  invalidYearIds={getInvalidYearIdsForScenario(scenario.id)}
                  tillageErrorYearIds={recalcValidationErrors?.get(scenario.id)?.tillageYearIds ?? []}
                  residueErrorYearIds={recalcValidationErrors?.get(scenario.id)?.residueYearIds ?? []}
                  addYearErrorMessage={getAddYearErrorMessage(scenario.id)}
                  validationErrorMessage={getValidationErrorMessage(scenario.id)}
                  mainCropOptions={mainCropOptions}
                  mainCropLabelLookup={mainCropLabelLookup}
                  isMainCropOptionsLoading={isPracticeCropsFetching}
                  coverCropTiming={coverCropTiming}
                />
              </div>
            ))}
          </ScenarioList>
        </div>

        {/* One footer for the whole section, not per scenario, and present whatever the results state. */}
        {onViewResults && (
          <button type="button" className="scenario-analysis-view-results" onClick={onViewResults} data-testid="scenario-analysis-view-results">
            {t("scenarioBuilder.seeIfWorthIt")}
            <Icon iconName="tabler/arrow-right" size="s" className="scenario-analysis-view-results-icon" />
          </button>
        )}

        <AddScenarioModal
          isVisible={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          onAddScenario={handleAddScenario}
          existingScenarios={modalScenarios}
          dataTestId="add-scenario-modal"
        />

        {renderCropDetailsDrawer()}
      </div>
    );
  }
);

export default ScenarioBuilder;
